/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Types for table columns
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.0.0
 */

import { IImportSubscribersLimitsTableTranslation } from '@/main/modules/internationalization/types/wizards/importSubscribersLimits';
import { EImportSubscribersLimitsField } from '@/main/pages/WizardImportSubscribersLimits/wizardImportSubscribersLimitsConstant';

import {
    ICommonPageTranslation,
    ICommonTranslation,
    IPageBillsTranslation,
    IPageExpensesAnalyzerTranslation,
    IPagePaymentsTranslation,
    IPagePersonalAccountTranslation,
    IPageSubscriberFixedTranslation,
    IPageSubscriberMobileTranslation,
} from '../../../modules/internationalization/types';
import { IAutoReportIdTableTranslation } from '../../../modules/internationalization/types/pages/autoReportId';
import { IPageCampaignInfoTableTranslation } from '../../../modules/internationalization/types/pages/campaignInfo';
import { IPageCashbackHistoryTranslation } from '../../../modules/internationalization/types/pages/cashback';
import {
    IPageSettingsAutoReportsTableTranslation,
    IPageSettingsAutoReportsTitleTranslation,
    IPageSettingsStatisticsTranslation,
} from '../../../modules/internationalization/types/pages/pageSettings';
import { IImportPersonalDataTableTranslation } from '../../../modules/internationalization/types/wizards/importPersonalData';
import { ILabelsImportTableTranslation } from '../../../modules/internationalization/types/wizards/labelsImport';
import { IProductSelectSubscribersTableTranslation } from '../../../modules/internationalization/types/wizards/productOfferings';
import {
    EAutoReportIdTableAccountField,
    EAutoReportIdTableSubscriberField,
} from '../../../pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import { BillsField, BillsTableFooterField } from '../../../pages/PageBills/pageBillsTypes';
import {
    ECampaignInfoTableAccountField,
    ECampaignInfoTableSubscriberField,
} from '../../../pages/PageCampaignInfo/components/Table/CampaignInfoTableCells';
import { CashbackHistoryTableField } from '../../../pages/PageCashback/types/cashbackHistoryTypes';
import {
    EExpensesAnalyzerField,
    EExpensesAnalyzerTableFooterField,
} from '../../../pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import { ELabelsImportField } from '../../../pages/PageLabelsImport/labelsImportConstants';
import {
    EPagePaymentsTableField,
    EPagePaymentsTableFooterField,
} from '../../../pages/PagePayments/const/PagePaymentsTable';
import { ESettingsAutoReportsField } from '../../../pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';
import { ESettingsStatisticsField } from '../../../pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableTypes';
import { EImportPersonalDataField } from '../../../pages/WizardImportPersonalData/wizardImportPersonalDataConstant';
import { EProductSubscribersTableField } from '../../../pages/WizardProductOfferings/wizardProductOfferingsConstants';
import { PersonalAccountField } from '../../../types/personalAccountTypes';
import { SubscriberFixedField } from '../../../types/subscriberFixedTypes';
import { SubscriberMobileField } from '../../../types/subscriberTypes';

// =========== TYPE =============== //
/**
 * @description Define name to table column
 * @type ColumnFieldType
 * @see TableColumnUtil util class
 */
export type ColumnFieldType =
    | BillsField
    | CashbackHistoryTableField
    | EAutoReportIdTableAccountField
    | EAutoReportIdTableSubscriberField
    | EExpensesAnalyzerField
    | EImportPersonalDataField
    | EImportSubscribersLimitsField
    | ELabelsImportField
    | EPagePaymentsTableField
    | EProductSubscribersTableField
    | ESettingsAutoReportsField
    | ESettingsStatisticsField
    | PersonalAccountField
    | SubscriberFixedField
    | SubscriberMobileField
    | ECampaignInfoTableAccountField
    | ECampaignInfoTableSubscriberField
    ;

/**
 * @description Define translation types to table column
 * @type ColumnTranslationType
 * @see TableColumnUtil util class
 */
export type ColumnTranslationType = (
    | ICommonPageTranslation<ILabelsImportTableTranslation>
    | ICommonPageTranslation<IImportPersonalDataTableTranslation>
    | ICommonPageTranslation<IImportSubscribersLimitsTableTranslation>
    | ICommonPageTranslation<IPageSettingsAutoReportsTableTranslation, IPageSettingsAutoReportsTitleTranslation>
    | ICommonPageTranslation<IProductSelectSubscribersTableTranslation>
    | IAutoReportIdTableTranslation
    | IPageBillsTranslation
    | IPageCampaignInfoTableTranslation
    | IPageCashbackHistoryTranslation
    | IPageExpensesAnalyzerTranslation
    | IPagePaymentsTranslation
    | IPagePersonalAccountTranslation
    | IPageSettingsStatisticsTranslation
    | IPageSubscriberFixedTranslation
    | IPageSubscriberMobileTranslation
    ) & ICommonTranslation;

/**
 * @description Define name to table column
 * @type ColumnFieldType
 * @see TableColumnUtil util class
 */
export type TableFooterColumnFieldType =
    BillsTableFooterField
    | EExpensesAnalyzerTableFooterField
    | EPagePaymentsTableFooterField;

// =========== ENUM =============== //

export enum EColumnFilter {
    filterDate = 'filterDate',
    filterLabelMultiSelect = 'filterLabelMultiSelect',
    filterMultiSelect = 'filterMultiSelect',
    filterRange = 'filterRange',
    filterSearchInput = 'filterSearchInput',
    filterSearch = 'filterSearch',
    filterSelect = 'filterSelect',
    filterTime = 'filterTime',
}

export enum EColumnCell {
    actionConflicts = 'actionConflicts',
    amount = 'amount',
    balance = 'balance',
    balanceScore = 'balanceScore',
    billsAccount = 'billsAccount',
    billsPayStatus = 'billsPayStatus',
    billsPeriod = 'billsPeriod',
    closeDate = 'closeDate',
    cost = 'cost',
    count = 'count',
    createDate = 'createDate',
    dateCell = 'dateCell',
    importPersonalDataStatus = 'importPersonalDataStatus',
    importSubscribersLimitsStatus = 'importSubscribersLimitsStatus',
    labels = 'labels',
    labelsImportConflict = 'labelsImportConflict',
    linkAccount = 'account',
    linkAccountExpenses = 'linkAccountExpenses',
    linkAccountNumber = 'linkAccountNumber',
    linkAction = 'linkAction',
    linkCampaignAccount = 'linkCampaignAccount',
    linkCampaignAccountNumber = 'linkCampaignAccountNumber',
    linkCampaignId = 'linkCampaignId',
    linkCampaignNumber = 'linkCampaignNumber',
    linkFixedAccount = 'linkFixedAccount',
    linkMobile = 'linkMobile',
    linkMobileCampaign = 'linkMobileCampaign',
    linkSettingsAutoReports = 'linkSettingsAutoReports',
    linkUnpaidBillsNumber = 'linkUnpaidBillsNumber',
    name = 'name',
    notEmpty = 'notEmpty',
    persDataStatusName = 'persDataStatusName',
    phoneText = 'phoneText',
    removeAutoReport = 'removeAutoReport',
    rowDeleteAction = 'rowDeleteAction',
    schedule = 'schedule',
    score = 'score',
    statusLabelsImport = 'statusLabelsImport',
    text = 'text',
    textDictionary = 'textDictionary',
    textHtml = 'textHtml',
    timeCell = 'timeCell',
    updateDate = 'updateDate',
}

export enum EColumnBalance {
    negative = 'negative',
    positive = 'positive',
    zero = 'zero',
}

export enum EColumnCellType {
    double = 'double',
    default = '',
}
